import { getStorageItem, removeStorageItem, setStorageItem } from 'utils/ui/storage';

/**
 * Per-tab record that THIS browsing context's own 3D deck published the stream id
 * for a session — i.e. this context is the stream host, not a dealer who joined a
 * stream someone else already started.
 *
 * Why a durable record is needed: `getSessionHostRole()` otherwise infers the role
 * from live session state (`hasStreamingId && streamActive`). That inference is only
 * correct the first time. Once this tab's own deck has published `aveSessionId`, any
 * fresh React root — the DCC host replaces the stream element mid-consultation — re-runs
 * the inference and mistakes the organizer for a late joiner (AVS-4452). The claim is the
 * fact the inference cannot recover on its own, so it has to outlive the React tree.
 *
 * Source of truth is an in-memory module variable; sessionStorage is a best-effort mirror
 * for reload survival — the same pattern as `inheritedSession.ts` and `voipTabId.ts`. The
 * mirror is read back ONLY after a genuine reload: "Duplicate tab" copies sessionStorage
 * wholesale, and an inherited copy would hand the privileged host role to a second context
 * that never published anything (AVS-4452 review F3).
 *
 * This module imports storage only. It must never import `state/session`, which imports
 * it back.
 */

const KEY = 'e3d-stream-host-sid';

let streamHostSessionId: string | undefined;

/**
 * True only when this document is a reload of itself. A duplicated tab reports 'navigate',
 * as does a fresh navigation, and neither may inherit the previous document's claim.
 * Anything unreadable answers false: the in-memory variable already carries the claim across
 * a React root replacement, which is the case this fix exists for, so declining to restore
 * costs nothing there.
 */
function isReloadNavigation(): boolean {
  const entries = globalThis.performance?.getEntriesByType?.('navigation') ?? [];
  const [navigation] = entries as PerformanceNavigationTiming[];
  return navigation?.type === 'reload';
}

function readMirroredClaim(): string | undefined {
  const stored = getStorageItem('sessionStorage', KEY);
  return typeof stored === 'string' && stored ? stored : undefined;
}

// Restore from sessionStorage on module initialisation (survives a hard reload only).
try {
  if (isReloadNavigation()) {
    streamHostSessionId = readMirroredClaim();
  } else {
    removeStorageItem('sessionStorage', KEY);
  }
} catch {
  // sessionStorage or performance blocked — in-memory only, acceptable degradation.
}

/**
 * Record that this context's own deck published the stream id for `sessionId`.
 * Call it from the deck's connect handler, and only when this deck is the publisher.
 *
 * Falsy / empty writes are no-ops. Last write wins: one tab hosts one session at a time.
 */
export function claimStreamHost(sessionId: string | undefined): void {
  if (!sessionId) return;
  streamHostSessionId = sessionId;
  try {
    setStorageItem('sessionStorage', KEY, sessionId);
  } catch {
    // sessionStorage blocked — in-memory record is sufficient.
  }
}

/**
 * Returns true when this context claimed the stream host role for exactly `sessionId`.
 *
 * Unlike `isInheritedSession`, a missing `sessionId` answers false rather than
 * pessimistically true. The claim grants a role; guessing in its favour would let a
 * half-loaded tab promote itself.
 */
export function isStreamHostForSession(sessionId?: string): boolean {
  return !!streamHostSessionId && streamHostSessionId === sessionId;
}

/** Call when this context leaves or restarts a session. */
export function clearStreamHost(): void {
  streamHostSessionId = undefined;
  try {
    removeStorageItem('sessionStorage', KEY);
  } catch {
    // sessionStorage blocked — in-memory clear is sufficient.
  }
}
